import { fg } from '@atlaskit/platform-feature-flags';
import {
	type T3pResource,
	type TSearch3pConfigAPIResponse,
} from '@atlassian/search-client/fetch-search-config';

import {
	type ProductKeys3P,
	ThirdPartyConfigs,
} from '../../../common/constants/3p-product-configs';
import { get3PLogoFromURL } from '../../../common/constants/icons';
import { allProductKeys } from '../../../common/constants/products';
import {
	type ConnectorSource,
	FEDERATED_CONNECTOR_SOURCE,
	type ForgeConnectorProductKey,
	FULL_CONNECTOR_SOURCE,
	isForgeConnectorProductKey,
	LITE_CONNECTOR_SOURCE,
	type PlatformIntegrationAri,
	registerForgeConnectorIcon,
	SMARTLINKS_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import { mapAtisToTypeFilterConfig } from '../../../common/utils/ati-to-filter-type/map-atis-to-type-filter-config';
import { registerForgeConnectorTypeMapping } from '../../../common/utils/filters/constants';

type ConnectorSourceMap = Map<string, Set<ConnectorSource>>;

export const getThirdPartyTypes: () => Record<string, string> = (): Record<string, string> => {
	// old sharepoint product key is put here (kept for backwards compatibility), as the new one is in the config
	const oldThirdPartyTypes: Record<string, string> = {
		'ai-3p-connector:sharepoint-domain': `sharepoint`,
		'ai-3p-connector:google-workspace': `drive`,
	};

	const thirdPartyTypes: Record<string, string> = {
		...oldThirdPartyTypes,
		// add third party types for dual connectors
		'lite-connector-gdrive': `drive`,
		'smartlinks-connector-gdrive': `drive`,
		'smartlinks-connector-gdrive-rsl': `drive`, // needed for relevance testing
		'smartlinks-connector-salesforce': `salesforce`,
		'smartlinks-connector-airtable': `airtable`,
		'smartlinks-connector-figma': `figma`,
		'smartlinks-connector-gitlab': `gitlab`,
		'smartlinks-connector-miro': `miro`,
		'smartlinks-connector-asana': `asana`,
		'smartlinks-connector-smartsheet': `smartsheet`,
		'smartlinks-connector-dropbox': `dropbox`,
		'smartlinks-connector-box': `box`,
		'smartlinks-connector-lucid': `lucid`,
		'smartlinks-connector-hubspot': `hubspot`,
		'smartlinks-connector-docusign': `docusign`,
		'smartlinks-connector-slack': `slack`,
		'smartlinks-connector-teams': `teams`,
		'smartlinks-connector-azure-devops': `azure-devops`,
		'smartlinks-connector-notion': `notion`,
		'smartlinks-connector-onedrive': `onedrive`,
		'smartlinks-connector-sharepoint': `sharepoint`,
	};

	ThirdPartyConfigs.forEach((config) => {
		if (config.resourceType) {
			thirdPartyTypes[config.resourceType] = config.id;
		}
	});

	return thirdPartyTypes;
};

const mapForgeConnectorToThirdPartyConfig = (
	forgeConnector: T3pResource,
): ThirdPartyConfigsWithAPIResponse | undefined => {
	// Validate required fields
	if (!forgeConnector.lightIcon || !forgeConnector.workspaceName) {
		return undefined;
	}

	const icon = get3PLogoFromURL(forgeConnector.lightIcon, forgeConnector.workspaceName, 'medium');
	const searchResultIcon = get3PLogoFromURL(
		forgeConnector.lightIcon,
		forgeConnector.providerId,
		'large',
	);

	const productKey = forgeConnector.type as ForgeConnectorProductKey;

	// Register the icon in the registry so ProductOrNounIcon can access it without props
	registerForgeConnectorIcon(productKey, icon);

	// Build type filter config
	const typeFilterConfig = forgeConnector.entityTypes
		? mapAtisToTypeFilterConfig(forgeConnector.entityTypes)
		: new Map();

	// Register the type mapping in the cache so universal type filter can use it
	if (fg('rovo-search-dynamic-forge-config')) {
		registerForgeConnectorTypeMapping(productKey, typeFilterConfig);
	}

	const config: ThirdPartyConfigsWithAPIResponse = {
		id: forgeConnector.type,
		displayName: forgeConnector.environmentType
			? `${forgeConnector.workspaceName} (${forgeConnector.environmentType})`
			: forgeConnector.workspaceName,
		resourceType: forgeConnector.type,
		datasourceId: forgeConnector.datasourceId,
		connectionId: forgeConnector.connectionId,
		providerId: forgeConnector.providerId,
		outboundAuthUrl: forgeConnector.outboundAuthUrl,
		integrationAri: forgeConnector.integrationId
			? (forgeConnector.integrationId as unknown as PlatformIntegrationAri)
			: (`ari:cloud:platform::integration/${forgeConnector.providerId}` as PlatformIntegrationAri),
		filters: ['lastUpdated', 'universalTypeFilter'],
		displayWorkspaceNameAsProductName: true,
		isEnabled: () => true,
		userNeedsOAuth: !forgeConnector.isUserOAuthed,
		requiresOauth: !forgeConnector.isUserOAuthed && !forgeConnector.outboundAuthUrl ? false : true,
		icon,
		searchResultIcon,
		typeFilterConfig,
		workspaceName: forgeConnector.workspaceName,
		connectorSources: [FULL_CONNECTOR_SOURCE],
	};

	return config;
};

export const map3pResourcesToProductKeys = (resources: TSearch3pConfigAPIResponse) => {
	const THIRD_PARTY_TYPES: Record<string, string> = getThirdPartyTypes();

	// Use a map to track connector sources for each providerId
	// This is useful for deduping search results for providers that are hybrid smartlink and full connectors
	const connectorSourceMap: ConnectorSourceMap = new Map();

	let productKeys: (ProductKeys3P | ForgeConnectorProductKey)[] = [];
	let thirdPartyConfigsWithAPIResponse: ThirdPartyConfigsBootstrap = {};

	resources.forEach((resource) => {
		// Handle Forge connectors first (if feature flag enabled)
		if (isForgeConnectorProductKey(resource.type) && fg('rovo-search-dynamic-forge-config')) {
			const config = mapForgeConnectorToThirdPartyConfig(resource);
			if (config) {
				const productKey = resource.type as ForgeConnectorProductKey;
				productKeys.push(productKey);
				thirdPartyConfigsWithAPIResponse[productKey] = config;
			}
			return; // Early return for Forge connectors
		}

		// Handle hardcoded connectors (existing logic)
		const productKey = (THIRD_PARTY_TYPES[resource.type] || '') as ProductKeys3P;
		if (!productKey) {
			return;
		}

		const { providerId, datasourceId } = resource;
		productKeys.push(productKey);
		let config = {} as ThirdPartyConfigsWithAPIResponse;
		const staticConfig = ThirdPartyConfigs.get(productKey);
		if (staticConfig) {
			const isSlackConnectorSyncTypeEnabled =
				fg('ai_3p_slack_connector_status_transition_enabled') && productKey === 'slack';

			let sourceType: ConnectorSource;

			if (resource.type.startsWith(SMARTLINKS_CONNECTOR_SOURCE)) {
				sourceType = SMARTLINKS_CONNECTOR_SOURCE;
			} else if (resource.type.startsWith(LITE_CONNECTOR_SOURCE)) {
				sourceType = LITE_CONNECTOR_SOURCE;
			} else if (isSlackConnectorSyncTypeEnabled) {
				sourceType =
					resource.connectorSyncType === 'federated'
						? FEDERATED_CONNECTOR_SOURCE
						: FULL_CONNECTOR_SOURCE;
			} else if (staticConfig.isFederated) {
				sourceType = FEDERATED_CONNECTOR_SOURCE;
			} else {
				sourceType = FULL_CONNECTOR_SOURCE;
			}

			connectorSourceMap.set(
				providerId,
				(connectorSourceMap.get(providerId) || new Set()).add(sourceType),
			);

			const existingConfig = thirdPartyConfigsWithAPIResponse[productKey];
			const isExistingConfigFullConnectorSource =
				existingConfig?.connectorSources?.includes(FULL_CONNECTOR_SOURCE) || false;

			const outboundAuthUrl =
				isExistingConfigFullConnectorSource && existingConfig?.outboundAuthUrl
					? existingConfig.outboundAuthUrl
					: resource.outboundAuthUrl;

			const userNeedsOAuth =
				isExistingConfigFullConnectorSource && existingConfig
					? existingConfig.userNeedsOAuth
					: !resource.isUserOAuthed;

			config = {
				...staticConfig,
				outboundAuthUrl,
				userNeedsOAuth,
				providerId,
				datasourceId,
				connectionId: resource.connectionId,
				workspaceName: resource.workspaceName,
				workspaceUrl: resource.workspaceUrl,
				connectorSources: Array.from(connectorSourceMap.get(providerId) || []),
				impersonationEnabled: resource.isImpersonationAuthEnabled,
				connectorCreatedAt: resource.connectorCreatedAt,
			};

			if (
				fg('ai3p_webcrawler_multi_domain_support') ||
				fg('ai3p_webcrawler_multi_domain_support_more_targets')
			) {
				const datasourceList = [];
				// Add the main config without the nested datasources
				// eslint-disable-next-line no-unused-vars
				const { datasources, ...configWithoutDatasources } = config;
				configWithoutDatasources.datasourceId = resource.datasourceId;
				configWithoutDatasources.connectionId = resource.connectionId;
				// TODO: Leave config level logic at the root of the datasource itself and datasource specific logic in the datasources -> https://hello.jira.atlassian.cloud/browse/DCCNT-1386
				const datasource = {
					...configWithoutDatasources,
					...resource,
					userNeedsOAuth: !resource.isUserOAuthed,
					integrationAri: resource.integrationId || configWithoutDatasources.integrationAri,
				};
				datasourceList.push(datasource);
				// Add third-party configs, also without the nested datasources
				const extraDatasources = thirdPartyConfigsWithAPIResponse[productKey]?.datasources || [];
				const cleanedExtraDatasources = extraDatasources.map(
					// eslint-disable-next-line no-unused-vars
					({ datasources, ...configWithoutDatasources }) => configWithoutDatasources,
				);
				datasourceList.push(...cleanedExtraDatasources);
				// Assign the cleaned datasourceList back to the main config
				config.datasources = datasourceList as ThirdPartyConfigsWithAPIResponse[];
			}
			thirdPartyConfigsWithAPIResponse[productKey] = config;
		}
	});

	// Filter product keys to only include valid ones
	// Note: Only filter hardcoded product keys, Forge connectors use their own type as product key
	const filteredProductKeys = productKeys.filter((productKey) => {
		if (isForgeConnectorProductKey(productKey)) {
			return true; // Forge connectors are always valid
		}
		// For hardcoded 3P connectors, check if they're in allProductKeys
		return allProductKeys.includes(productKey as ProductKeys3P);
	});

	return { productKeys: filteredProductKeys, thirdPartyConfigs: thirdPartyConfigsWithAPIResponse };
};
